import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { SettingsCard } from "../SettingsCard";
import { SettingsAPI, ClinicExportJob } from "../../../services/settings.service";
import { StatusBadge } from "../../../components/ui/StatusBadge";
import { UsersIcon, CalendarIcon, CapsuleIcon } from "../../../components/icons";
import {
  ClockIcon,
  DocumentIcon,
  DownloadIcon,
  MoreVerticalIcon,
  WarningTriangleIcon,
} from "../../../components/icons/exportIcons";
import {
  formatBytes,
  formatDateTime,
  formatTimeRemaining,
  scopeLabel,
  statusToBadgeVariant,
} from "../../../utils/clinicExport";
import { RequestExportModal } from "./RequestExportModal";

const PRIMARY_BTN =
  "inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition";
const SECONDARY_BTN =
  "inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 transition";

const STAT_KEYS: { key: string; label: string; icon: React.ReactNode }[] = [
  { key: "patients", label: "Patients", icon: <UsersIcon /> },
  { key: "visits", label: "Visits", icon: <CalendarIcon /> },
  { key: "prescriptions", label: "Prescriptions", icon: <CapsuleIcon /> },
  { key: "clinic_documents", label: "Documents (PDF)", icon: <DocumentIcon /> },
];

export const DataPrivacy: React.FC = () => {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState<ClinicExportJob[] | null>(null);
  const [clinicName, setClinicName] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    SettingsAPI.listClinicExports()
      .then(setJobs)
      .catch(() => setError("Could not load export history."));
  }, []);

  useEffect(() => {
    load();
    SettingsAPI.getClinicSettings()
      .then(data => setClinicName(data.name))
      .catch(() => {});
  }, [load]);

  const inFlight = jobs?.find(j => j.status === "pending" || j.status === "running") ?? null;
  const latestCompleted = jobs?.find(j => j.status === "completed") ?? null;

  const handleStarted = (job: ClinicExportJob) => {
    setModalOpen(false);
    navigate(`/settings/export/${job.job_id}`);
  };

  const handleCancel = async (jobId: string) => {
    await SettingsAPI.cancelClinicExport(jobId);
    load();
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 className="text-2xl font-semibold text-gray-900">Data &amp; Privacy</h2>
          <p className="text-sm text-gray-500 mt-1">
            Export, archive and closure for {clinicName || "your clinic"}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {inFlight ? (
            <span className="text-sm text-gray-500">An export is already running</span>
          ) : (
            <a href="#export-history" className={SECONDARY_BTN}>
              <ClockIcon /> Export History
            </a>
          )}
          <button
            className={PRIMARY_BTN}
            disabled={!!inFlight}
            onClick={() => setModalOpen(true)}
          >
            <DownloadIcon /> Request Export
          </button>
        </div>
      </div>

      {error && (
        <p className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-4 py-3">
          {error}
        </p>
      )}

      {inFlight ? (
        <>
          <div className="flex gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4">
            <WarningTriangleIcon className="text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-amber-800">
                Only one export can run at a time for a clinic.
              </p>
              <p className="text-sm text-amber-700 mt-0.5">
                Wait for the current job to finish, or cancel it, before requesting another
                archive.
              </p>
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-slate-100 p-6">
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-full bg-gradient-to-br from-cyan-400 to-blue-500 flex items-center justify-center text-white flex-shrink-0">
                  <ClockIcon />
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-gray-900">Export In Progress</h3>
                  <p className="text-xs text-gray-500 mt-0.5">
                    Started by {inFlight.requested_by_name ?? "a clinic admin"} at{" "}
                    {formatDateTime(inFlight.started_at ?? inFlight.requested_at)} ·{" "}
                    {scopeLabel(inFlight.include_documents)} · job {inFlight.job_id.slice(0, 8)}
                  </p>
                </div>
              </div>
              <StatusBadge variant={statusToBadgeVariant(inFlight.status)} label={inFlight.status} />
            </div>

            <div className="mt-5 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <a
                  href={`/settings/export/${inFlight.job_id}`}
                  className={SECONDARY_BTN}
                >
                  View Current Export
                </a>
                <button className={SECONDARY_BTN} onClick={() => handleCancel(inFlight.job_id)}>
                  Cancel Export
                </button>
              </div>
            </div>
          </div>
        </>
      ) : (
        <SettingsCard
          title="Full Data Export"
          icon={<DownloadIcon />}
          headerRight={<StatusBadge variant="audit-logged" />}
        >
          <p className="text-sm text-gray-600">
            Request a complete, portable copy of every record in this clinic. Kaagazy prepares
            the archive in the background and emails you a download link when it is ready — you
            do not need to keep this page open.
          </p>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-5">
            {STAT_KEYS.map(stat => (
              <div key={stat.key} className="rounded-xl border border-gray-100 p-4">
                <div className="w-8 h-8 rounded-full bg-blue-50 text-blue-600 flex items-center justify-center mb-3">
                  {stat.icon}
                </div>
                <p className="text-xl font-semibold text-gray-900">
                  {latestCompleted?.row_count_manifest?.[stat.key]?.toLocaleString() ?? "—"}
                </p>
                <p className="text-xs text-gray-500">{stat.label}</p>
              </div>
            ))}
          </div>

          <p className="text-xs text-gray-500 mt-4">
            One CSV or JSONL file per table · original PDFs copied as uploaded, not regenerated ·
            row-count manifest included · plain CSV, JSON and PDF only.
          </p>
        </SettingsCard>
      )}

      <div className="rounded-xl border border-blue-100 bg-blue-50 p-4">
        <p className="text-sm font-medium text-blue-800">
          Exports contain PII/PHI. Store the file securely.
        </p>
        <p className="text-sm text-blue-700 mt-1">
          Aadhaar numbers are masked and payment gateway credentials are excluded. Every request,
          completion and link issue is audit-logged against your user.
        </p>
      </div>

      <div id="export-history" className="bg-white rounded-2xl border border-slate-200 overflow-hidden scroll-mt-6">
        <div className="px-5 py-4 border-b border-slate-100">
          <h3 className="text-sm font-semibold text-gray-900">Previous Exports</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] font-semibold uppercase tracking-wider text-slate-500 border-b border-slate-100">
                <th className="px-5 py-3">Requested</th>
                <th className="px-5 py-3">Requested By</th>
                <th className="px-5 py-3">Scope</th>
                <th className="px-5 py-3">Size</th>
                <th className="px-5 py-3">Status</th>
                <th className="px-5 py-3">Download Link</th>
                <th className="px-5 py-3" />
              </tr>
            </thead>
            <tbody>
              {(jobs ?? []).map(job => (
                <tr key={job.job_id} className="border-b border-slate-50 last:border-0">
                  <td className="px-5 py-3 text-gray-700">{formatDateTime(job.requested_at)}</td>
                  <td className="px-5 py-3 text-gray-700">{job.requested_by_name ?? "—"}</td>
                  <td className="px-5 py-3 text-gray-500">{scopeLabel(job.include_documents)}</td>
                  <td className="px-5 py-3 text-gray-500">{formatBytes(job.archive_size_bytes)}</td>
                  <td className="px-5 py-3">
                    <StatusBadge variant={statusToBadgeVariant(job.status)} label={job.status} />
                  </td>
                  <td className="px-5 py-3 text-gray-500">
                    {job.status === "completed"
                      ? `expires in ${formatTimeRemaining(job.download_url_expires_at)}`
                      : job.status === "failed" || job.status === "cancelled"
                        ? "not issued"
                        : "—"}
                  </td>
                  <td className="px-5 py-3 text-right">
                    <button
                      className="text-gray-400 hover:text-gray-600"
                      onClick={() => navigate(`/settings/export/${job.job_id}`)}
                    >
                      <MoreVerticalIcon />
                    </button>
                  </td>
                </tr>
              ))}
              {jobs && jobs.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-5 py-8 text-center text-sm text-gray-400">
                    No exports requested yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {modalOpen && (
        <RequestExportModal
          clinicName={clinicName}
          latestManifest={latestCompleted?.row_count_manifest ?? null}
          onClose={() => setModalOpen(false)}
          onStarted={handleStarted}
        />
      )}
    </div>
  );
};
